import { Component, EventEmitter, Input, Output, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { TicketService } from '../../../core/services/ticket.service';
import { 
  TicketDepartment, 
  TicketCategory, 
  TicketRelatedToType, 
  CreateTicketPayload 
} from '../../../core/models/ticket.model';

@Component({
  selector: 'app-ticket-creation-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './ticket-creation-modal.html',
})
export class TicketCreationModalComponent implements OnInit, OnChanges {
  @Input() show: boolean = false;
  // Pre-fill related entity if opened from a specific page (e.g. Unit Details or Booking)
  @Input() defaultRelatedType?: string;
  @Input() defaultRelatedId?: string;

  @Output() close = new EventEmitter<void>();
  @Output() ticketCreated = new EventEmitter<any>();

  ticketForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';

  departments = Object.values(TicketDepartment);
  categories = Object.values(TicketCategory);
  relatedTypes = Object.values(TicketRelatedToType);

  private fb = inject(FormBuilder);
  private ticketService = inject(TicketService);

  ngOnInit() {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.ticketForm) return;

    if (changes['defaultRelatedType'] || changes['defaultRelatedId']) {
      this.syncDefaultRelatedEntity();
    }
  }

  get hasLockedRelatedEntity(): boolean {
    return !!this.defaultRelatedType && !!this.defaultRelatedId;
  }

  private initForm() {
    this.ticketForm = this.fb.group({
      subject: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(20), Validators.maxLength(1000)]],
      department: ['', Validators.required],
      category: ['', Validators.required],
      relatedType: [this.defaultRelatedType || '', Validators.required],
      relatedId: [this.defaultRelatedId || '', Validators.required],
    });

    this.syncDefaultRelatedEntity();
  }

  private syncDefaultRelatedEntity() {
    const relatedTypeControl = this.ticketForm.get('relatedType');
    const relatedIdControl = this.ticketForm.get('relatedId');

    relatedTypeControl?.setValue(this.defaultRelatedType || '');
    relatedIdControl?.setValue(this.defaultRelatedId || '');

    if (this.hasLockedRelatedEntity) {
      relatedTypeControl?.disable({ emitEvent: false });
      relatedIdControl?.disable({ emitEvent: false });
    } else {
      relatedTypeControl?.enable({ emitEvent: false });
      relatedIdControl?.enable({ emitEvent: false });
    }
  }

  closeModal() {
    this.ticketForm.reset({
      relatedType: this.defaultRelatedType || '',
      relatedId: this.defaultRelatedId || ''
    });
    this.syncDefaultRelatedEntity();
    this.errorMessage = '';
    this.close.emit();
  }

  onSubmit() {
    if (this.ticketForm.invalid) {
      this.ticketForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const formValue = this.ticketForm.getRawValue();
    const payload: CreateTicketPayload = {
      subject: formValue.subject,
      description: formValue.description,
      department: formValue.department,
      category: formValue.category,
      relatedTo: {
        type: formValue.relatedType,
        id: formValue.relatedId
      }
    };

    this.ticketService.createTicket(payload).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.ticketCreated.emit(res.ticket);
        this.closeModal();
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Error creating ticket:', err);
        this.errorMessage = this.getErrorMessage(err, 'Failed to create ticket. Please try again.');
      }
    });
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.message || error?.error?.message || fallback;
  }
}
